import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { StatusBadge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { toast } from 'sonner'
import { RefreshCw } from 'lucide-react'

function RecalculateRecommendationsCard() {
  const queryClient = useQueryClient()

  const recalculateMutation = useMutation({
    mutationFn: () =>
      api.post<{ created: number; m365Tenants: number }>('/recommendation-rules/recalculate'),
    onSuccess: (data) => {
      toast.success(
        data.created > 0
          ? `Recomendaciones recalculadas: ${data.created} nuevas generadas`
          : 'Recomendaciones recalculadas con las reglas actuales',
      )
      queryClient.invalidateQueries({ queryKey: ['recommendations'] })
    },
    onError: (err) => toast.error((err as Error).message),
  })

  return (
    <Card>
      <CardHeader><CardTitle>Recomendaciones</CardTitle></CardHeader>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          Vuelve a evaluar las recomendaciones pendientes aplicando las reglas y excepciones
          configuradas actualmente. Las pendientes que ya no cumplan las reglas se resolveran
          automaticamente y se generaran nuevas si procede.
        </p>
        <Button
          size="sm"
          onClick={() => recalculateMutation.mutate()}
          loading={recalculateMutation.isPending}
        >
          <RefreshCw className="h-3.5 w-3.5" /> Recalcular recomendaciones
        </Button>
      </div>
    </Card>
  )
}

export function SettingsGeneralPage() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'tenant_admin' || user?.role === 'super_admin'

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<{ id: string; name: string; slug: string; plan: string; status: string; max_users: number }>('/settings'),
  })

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle>Organizacion</CardTitle></CardHeader>
        <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
          <div>
            <p style={{ color: 'var(--color-text-tertiary)' }}>Nombre</p>
            <p className="font-medium">{settings?.name ?? '—'}</p>
          </div>
          <div>
            <p style={{ color: 'var(--color-text-tertiary)' }}>Plan</p>
            <StatusBadge status={settings?.plan ?? 'trial'} />
          </div>
          <div>
            <p style={{ color: 'var(--color-text-tertiary)' }}>Slug</p>
            <p className="font-medium">{settings?.slug ?? '—'}</p>
          </div>
          <div>
            <p style={{ color: 'var(--color-text-tertiary)' }}>Limite usuarios</p>
            <p className="font-medium">{settings?.max_users ?? '—'}</p>
          </div>
        </div>
      </Card>

      {isAdmin && <RecalculateRecommendationsCard />}
    </div>
  )
}
